import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
	Alert,
	Badge,
	Box,
	Button,
	Group,
	NumberInput,
	Paper,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from '@mantine/core'
import DeviceFloppy from 'tabler-icons-react/dist/icons/device-floppy'
import { getOrgDetails } from '../services/actions/orgActions'
import {
	getOrgCommerceControlSettings,
	updateOrgCommercePlatformFees,
} from '../services/actions/controlActions'

const defaultForm = {
	platformFeePercent: 0,
	platformFeeFixedDollars: 0,
}

const statusDetails = {
	ENABLED: {
		color: 'teal',
		label: 'Ready',
		description: 'Stripe can accept card payments and send payouts.',
	},
	PENDING: {
		color: 'yellow',
		label: 'Onboarding',
		description: 'Stripe setup has started and needs to be completed.',
	},
	RESTRICTED: {
		color: 'orange',
		label: 'Needs action',
		description: 'Stripe needs more information before commerce is ready.',
	},
	DISABLED: {
		color: 'red',
		label: 'Disabled',
		description: 'Stripe has disabled this connected account.',
	},
	NOT_STARTED: {
		color: 'gray',
		label: 'Not started',
		description: 'Create a Stripe connected account from the org admin commerce page.',
	},
}

const centsToDollars = (value) => Number(value || 0) / 100
const dollarsToCents = (value) => Math.round(Number(value || 0) * 100)
const bpsToPercent = (value) => Number((Number(value || 0) / 100).toFixed(2))
const percentToBps = (value) => Math.round(Number(value || 0) * 100)

const formatMoney = (cents) =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(centsToDollars(cents))

const toNumber = (value, fallback = 0) => {
	const numeric = Number(value)
	return Number.isFinite(numeric) ? numeric : fallback
}

const formFromSettings = (settings = {}) => ({
	platformFeePercent: bpsToPercent(settings.platformFeeBps),
	platformFeeFixedDollars: centsToDollars(settings.platformFeeFixedCents),
})

const ControlOrgCommercePage = () => {
	const { orgSlug } = useParams()
	const [org, setOrg] = useState(null)
	const [payload, setPayload] = useState(null)
	const [form, setForm] = useState(defaultForm)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState('')
	const [notice, setNotice] = useState('')

	const applyPayload = useCallback((nextPayload) => {
		setPayload(nextPayload)
		setForm(formFromSettings(nextPayload?.settings || {}))
	}, [])

	const loadCommerceState = useCallback(async () => {
		setLoading(true)
		setError('')
		try {
			const orgData = await getOrgDetails(orgSlug)
			setOrg(orgData)
			const commerceData = await getOrgCommerceControlSettings(orgData.id)
			applyPayload(commerceData)
		} catch (err) {
			console.error('Failed to load control commerce settings:', err)
			setError(err.message || 'Failed to load commerce settings.')
		} finally {
			setLoading(false)
		}
	}, [applyPayload, orgSlug])

	useEffect(() => {
		loadCommerceState()
	}, [loadCommerceState])

	const updateForm = (field, value) => {
		setForm((current) => ({
			...current,
			[field]: value,
		}))
	}

	const savePlatformFees = async () => {
		if (!org?.id) return
		setSaving(true)
		setError('')
		setNotice('')
		try {
			const data = await updateOrgCommercePlatformFees(org.id, {
				platformFeeType: 'BLENDED',
				platformFeeBps: percentToBps(form.platformFeePercent),
				platformFeeFixedCents: dollarsToCents(form.platformFeeFixedDollars),
			})
			applyPayload(data)
			setNotice(data?.message || 'LiveHQ platform fee settings saved.')
		} catch (err) {
			console.error('Failed to save commerce platform fees:', err)
			setError(err.message || 'Failed to save LiveHQ platform fees.')
		} finally {
			setSaving(false)
		}
	}

	if (loading) {
		return (
			<Box px='md' py='md'>
				Loading commerce controls...
			</Box>
		)
	}

	const settings = payload?.settings || {}
	const status = settings.onboardingStatus || 'NOT_STARTED'
	const statusInfo = statusDetails[status] || statusDetails.NOT_STARTED

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='xs'>
				Commerce Controls
			</Title>
			<Text c='dimmed' mb='md'>
				{org?.name || payload?.org?.name || orgSlug}
			</Text>

			{error && (
				<Alert color='red' mb='sm'>
					{error}
				</Alert>
			)}
			{notice && (
				<Alert color='teal' mb='sm'>
					{notice}
				</Alert>
			)}

			<Stack spacing='md'>
				<Paper withBorder p='md' radius='md'>
					<Group spacing='xs' mb='xs'>
						<Title order={4}>Stripe Commerce Status</Title>
						<Badge color={statusInfo.color} variant='light'>
							{statusInfo.label}
						</Badge>
					</Group>
					<Text size='sm' c='dimmed' mb='md'>
						{statusInfo.description}
					</Text>

					<SimpleGrid
						cols={3}
						breakpoints={[
							{ maxWidth: 'md', cols: 1 },
						]}
					>
						<Box>
							<Text size='sm' c='dimmed'>
								Connected account
							</Text>
							<Text fw={600}>
								{settings.stripeConnectedAccountId || 'Not created'}
							</Text>
						</Box>
						<Box>
							<Text size='sm' c='dimmed'>
								Card charges
							</Text>
							<Badge
								color={settings.stripeChargesEnabled ? 'teal' : 'gray'}
								variant='light'
							>
								{settings.stripeChargesEnabled ? 'Enabled' : 'Not enabled'}
							</Badge>
						</Box>
						<Box>
							<Text size='sm' c='dimmed'>
								Payouts
							</Text>
							<Badge
								color={settings.stripePayoutsEnabled ? 'teal' : 'gray'}
								variant='light'
							>
								{settings.stripePayoutsEnabled ? 'Enabled' : 'Not enabled'}
							</Badge>
						</Box>
					</SimpleGrid>
				</Paper>

				<Paper withBorder p='md' radius='md'>
					<Group position='apart' align='flex-start' mb='md'>
						<div>
							<Title order={4}>LiveHQ Platform Fee</Title>
							<Text size='sm' c='dimmed'>
								Applied as the Stripe Connect application fee for on-site commerce payments.
							</Text>
						</div>
						<Button
							leftIcon={<DeviceFloppy size={16} />}
							onClick={savePlatformFees}
							loading={saving}
						>
							Save Platform Fee
						</Button>
					</Group>

					<Stack spacing='md'>
						<SimpleGrid
							cols={2}
							breakpoints={[
								{ maxWidth: 'md', cols: 1 },
							]}
						>
							<NumberInput
								label='Platform fee percent'
								value={form.platformFeePercent}
								onChange={(value) =>
									updateForm('platformFeePercent', toNumber(value))
								}
								min={0}
								max={100}
								precision={2}
								step={0.25}
							/>
							<NumberInput
								label='Per-transaction fixed fee'
								value={form.platformFeeFixedDollars}
								onChange={(value) =>
									updateForm('platformFeeFixedDollars', toNumber(value))
								}
								min={0}
								precision={2}
								step={0.25}
							/>
						</SimpleGrid>

						<Text size='sm' c='dimmed'>
							Current platform fee per transaction: {toNumber(form.platformFeePercent).toFixed(2)}% +{' '}
							{formatMoney(dollarsToCents(form.platformFeeFixedDollars))}
						</Text>
					</Stack>
				</Paper>
			</Stack>
		</Box>
	)
}

export default ControlOrgCommercePage
